import React from "react";
import { Box, Link, PaletteMode, Tooltip, useTheme } from "@mui/material";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import SouthEastRoundedIcon from "@mui/icons-material/SouthEastRounded";
import {
  fmtDate,
  fmtInt,
  fmtMoney,
  fmtMoneyDetailed,
  csvNum,
  fmtNum,
  fmtPct,
} from "../../../utils/format";
import {
  getInternalPlatformDisplayName,
  isDv360InternalPlatform,
} from "../../../utils/internalPlatformUtils";
import { VOLUME_LABEL } from "../../cost-analysis/costAnalysisFormat";
import { EnrichedRow } from "./derive";

// ──────────────────────────────────────────────────────────────────────────────
// Status display helpers (mirrors Wip.tsx)
// ──────────────────────────────────────────────────────────────────────────────

type WIPStatus = EnrichedRow["status"];

export const STATUS_LABEL: Record<WIPStatus, string> = {
  SIN_FECHAS: "Sin fechas",
  PENDIENTE_INICIO: "Pendiente de inicio",
  FINALIZADA: "Finalizada",
  INICIO_PENDIENTE: "Inicio pendiente",
  ATRASADO: "Atrasada",
  EN_RITMO: "En ritmo",
  ADELANTADO: "Adelantada",
};

export const STATUS_COLOR: Record<WIPStatus, string> = {
  SIN_FECHAS: "#6b7280",
  PENDIENTE_INICIO: "#64748b",
  FINALIZADA: "#8b5cf6",
  INICIO_PENDIENTE: "#2563eb",
  ATRASADO: "#ef4444",
  EN_RITMO: "#10b981",
  ADELANTADO: "#f59e0b",
};

const INVESTMENT_PAYMENT_LABEL: Record<string, string> = {
  CLIENTE: "Cliente",
  TI_MEDIA_GROUP_SL: "Ti Media Group SL",
};

/** Where the line really runs (its internal campaign). The DV360 variants
 *  (_VIDEO_BA, _DISPLAY_BA…) all read as DV360. "" without an internal campaign. */
export const realPlatformLabel = (
  r: Pick<EnrichedRow, "realPlatform" | "isDirectBuy">
): string => {
  if (r.realPlatform)
    return isDv360InternalPlatform(r.realPlatform)
      ? "DV360"
      : getInternalPlatformDisplayName(r.realPlatform);
  return r.isDirectBuy ? "Compra directa" : "";
};

const MonoSpan: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size,
}) => (
  <Box
    component="span"
    sx={{
      fontFamily: "monospace",
      ...(size ? { fontSize: size } : {}),
      color: "grey.700",
    }}
  >
    {children}
  </Box>
);

const StatusChip: React.FC<{ status: WIPStatus }> = ({ status }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      px: 1.2,
      py: 0.4,
      borderRadius: 1,
      fontSize: 11,
      fontWeight: 600,
      color: "#fff",
      backgroundColor: STATUS_COLOR[status],
      whiteSpace: "nowrap",
    }}
  >
    {STATUS_LABEL[status]}
  </Box>
);

// ──────────────────────────────────────────────────────────────────────────────
// Proyección: volumen al cierre frente al previsto
// ──────────────────────────────────────────────────────────────────────────────

/** Margen de la proyección, el mismo que separa "En ritmo" de atrasada o adelantada. */
const PROJECTION_TOLERANCE = 0.1;

export type ProjectionOutcome = "CORRECTA" | "SE_PASA" | "NO_LLEGA";

export const PROJECTION_LABEL: Record<ProjectionOutcome, string> = {
  CORRECTA: "Correcta",
  SE_PASA: "Se pasa",
  NO_LLEGA: "No llega",
};

export const PROJECTION_COLOR: Record<ProjectionOutcome, string> = {
  CORRECTA: STATUS_COLOR.EN_RITMO,
  SE_PASA: STATUS_COLOR.ADELANTADO,
  NO_LLEGA: STATUS_COLOR.ATRASADO,
};

const PROJECTION_ARROW: Record<ProjectionOutcome, typeof EastRoundedIcon> = {
  CORRECTA: EastRoundedIcon,
  SE_PASA: NorthEastRoundedIcon,
  NO_LLEGA: SouthEastRoundedIcon,
};

export const projectionOutcome = (variation: number): ProjectionOutcome =>
  variation > PROJECTION_TOLERANCE
    ? "SE_PASA"
    : variation < -PROJECTION_TOLERANCE
      ? "NO_LLEGA"
      : "CORRECTA";

/** +11%, −4%, 0% */
export const fmtProjection = (variation: number): string => {
  const pct = Math.round(variation * 100);
  if (pct === 0) return "0%";
  return `${pct > 0 ? "+" : "−"}${Math.abs(pct)}%`;
};

/** Gris neutro del aviso "Datos a 0": no es ni bueno ni malo, falta saber. */
export const PROJECTION_ZERO_COLOR = STATUS_COLOR.SIN_FECHAS;
export const PROJECTION_ZERO_LABEL = "Datos a 0";

const chipSx = (backgroundColor: string) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.4,
  px: 1,
  py: 0.4,
  borderRadius: 1,
  fontSize: 11,
  fontWeight: 600,
  color: "#fff",
  backgroundColor,
  whiteSpace: "nowrap",
});

const ProjectionChip: React.FC<{ row: EnrichedRow }> = ({ row }) => {
  if (row.projectionZeroData)
    return (
      <Tooltip
        title={`Ayer llegaron 0 ${VOLUME_LABEL[row.objective] ?? ""}: la línea está parada o aún no se ha sincronizado. Si sigue así acabaría con ${fmtInt(row.accumulatedVolumeUntilYesterday)} de ${fmtInt(row.targetVolume)}.`}
      >
        <Box component="span" sx={chipSx(PROJECTION_ZERO_COLOR)}>
          {PROJECTION_ZERO_LABEL}
        </Box>
      </Tooltip>
    );
  const variation = row.projectionVariation;
  if (variation === null) return <>—</>;
  const outcome = projectionOutcome(variation);
  const Arrow = PROJECTION_ARROW[outcome];
  return (
    <Tooltip
      title={`Acabaría con ${fmtInt(row.projectedVolume ?? 0)} de ${fmtInt(row.targetVolume)} ${VOLUME_LABEL[row.objective] ?? ""}`}
    >
      <Box component="span" sx={chipSx(PROJECTION_COLOR[outcome])}>
        <Arrow sx={{ fontSize: 14 }} />
        {PROJECTION_LABEL[outcome]}
        <Box component="span" sx={{ fontWeight: 800 }}>
          {fmtProjection(variation)}
        </Box>
      </Box>
    </Tooltip>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Column definition
// ──────────────────────────────────────────────────────────────────────────────

export type SectorId =
  | "identification"
  | "brief"
  | "kpis"
  | "alert"
  | "pacing"
  | "others";

export const SECTOR_LABEL: Record<SectorId, string> = {
  identification: "Identificación",
  brief: "Brief",
  kpis: "KPIs Generales",
  alert: "Alerta",
  pacing: "Pacing Inversión",
  others: "Otros",
};

/** Conditional-formatting outcome for a metric cell */
export type Tone = "good" | "warn" | "bad";

/** color = text color, bg = subtle cell background.
 *  Keyed by palette mode: the light-mode inks (dark green/amber/red) are
 *  unreadable on a dark surface, so dark mode uses the light end of the same
 *  hues over a slightly stronger tint. */
export const TONE_STYLE: Record<
  PaletteMode,
  Record<Tone, { color: string; bg: string }>
> = {
  light: {
    good: { color: "#047857", bg: "rgba(16, 185, 129, 0.24)" },
    warn: { color: "#b45309", bg: "rgba(245, 158, 11, 0.26)" },
    bad: { color: "#b91c1c", bg: "rgba(239, 68, 68, 0.24)" },
  },
  dark: {
    good: { color: "#6ee7b7", bg: "rgba(16, 185, 129, 0.32)" },
    warn: { color: "#fcd34d", bg: "rgba(245, 158, 11, 0.34)" },
    bad: { color: "#fca5a5", bg: "rgba(239, 68, 68, 0.34)" },
  },
};

/** Background for the ideal-margin cells (the margin the real cost asks for),
 *  so they read apart from the planned column they sit next to. */
export const IDEAL_BG: Record<PaletteMode, string> = {
  light: "rgba(139, 92, 246, 0.12)",
  dark: "rgba(167, 139, 250, 0.2)",
};

/** Background of a direct-buy row: a line with no API connection whose
 *  metrics are typed in by hand. Replaces the status tint so it reads apart. */
export const DIRECT_BUY_BG: Record<PaletteMode, string> = {
  light: "rgba(13, 148, 136, 0.16)",
  dark: "rgba(45, 212, 191, 0.18)",
};

/** Accent of a direct-buy row (left border, legend swatch). */
export const DIRECT_BUY_ACCENT = "#0d9488";

/** Border of a cell that can be edited in place. */
export const EDITABLE_BORDER: Record<PaletteMode, string> = {
  light: "#7c3aed",
  dark: "#a78bfa",
};

/** Subtle background for columns flagged as important (emphasis). Dark mode
 *  needs a touch more alpha and a lighter blue to register at all against
 *  #1E1E1E. */
export const EMPHASIS_BG: Record<PaletteMode, string> = {
  light: "rgba(59, 130, 246, 0.13)",
  dark: "rgba(96, 165, 250, 0.2)",
};

/** Real metrics of a direct buy that are typed in by hand in the WIP. */
export type ManualMetricField =
  | "impressions"
  | "clicks"
  | "completeViews"
  | "conversions"
  | "uniqueUsers"
  | "platformInversion";

export interface ColumnDef {
  /** Stable key used for visibility persistence */
  id: string;
  sector: SectorId;
  label: string;
  align?: "left" | "right";
  /** false = hidden by default (sector "others") */
  defaultVisible: boolean;
  render: (row: EnrichedRow) => React.ReactNode;
  csvValue: (row: EnrichedRow) => string | number;
  /** Conditional formatting: maps a row to a tone (color/bg). */
  tone?: (row: EnrichedRow) => Tone | undefined;
  /** Important column — rendered with a subtle accent background. */
  emphasis?: boolean;
  /** Ideal-margin column: what the real cost asks for (tinted). */
  ideal?: boolean;
  /** Editable in place (the table decides per row whether it really is).
   *  "manual" = a real metric typed in by hand on direct-buy rows. */
  editable?: "margin" | "manual";
  /** With editable "manual": the field it writes and how it is typed. */
  manualField?: ManualMetricField;
  /** Carries the "accept ideal margin" button (the table renders it). */
  action?: "acceptIdeal";
  /** Frozen to the left during horizontal scroll. */
  pinned?: boolean;
  /** Minimum width (px) when resizing. */
  minWidth?: number;
  /** Initial width (px) under fixed table layout. */
  defaultWidth?: number;
  /** Only admins see it: in the table, the column menu, the info dialog and the CSV. */
  adminOnly?: boolean;
}

/** Tone helpers — shared thresholds for conditional formatting. Exported so
 *  the footer totals (columnTotals.ts) colour-code with the same thresholds. */
export const bySign = (n: number): Tone | undefined =>
  !Number.isFinite(n) || n === 0 ? undefined : n > 0 ? "good" : "bad";

/** Closeness to a 1.0 (100%) target: ±10% good, ±25% warn, else bad. */
export const byPacingTarget = (ratio: number): Tone | undefined => {
  if (!Number.isFinite(ratio)) return undefined;
  const dev = Math.abs(ratio - 1);
  if (dev <= 0.1) return "good";
  if (dev <= 0.25) return "warn";
  return "bad";
};

/** Volume completion bands: ≥0.9 good, 0.7–0.9 warn, <0.7 bad. */
export const byVolumeBand = (ratio: number): Tone | undefined => {
  if (!Number.isFinite(ratio)) return undefined;
  if (ratio >= 0.9) return "good";
  if (ratio >= 0.7) return "warn";
  return "bad";
};

/** Default column width (px) inferred from alignment/content type. */
export function defaultColumnWidth(col: ColumnDef): number {
  if (col.defaultWidth) return col.defaultWidth;
  if (col.id === "status") return 130;
  if (col.align === "right") return 110; // numbers / money / pct
  return 160; // text
}

/** Real platform spend against what was due by yesterday (same thresholds as
 *  Pacing Inv. %). undefined while nothing was due yet. */
export const spendTone = (r: EnrichedRow): Tone | undefined =>
  r.expectedPlatformInvestmentUntilYesterday > 0
    ? byPacingTarget(
        r.totalPlatformCost / r.expectedPlatformInvestmentUntilYesterday
      )
    : undefined;
/** Real volume until yesterday against what was due by then (same bands as
 *  % Vol. Obtenido). undefined while nothing was due yet. */
export const volumeTone = (r: EnrichedRow): Tone | undefined =>
  r.expectedVolumeUntilYesterday > 0
    ? byVolumeBand(
        r.accumulatedVolumeUntilYesterday / r.expectedVolumeUntilYesterday
      )
    : undefined;

interface ProgressBarProps {
  /** What has been reached so far (spend, volume…). */
  value: number;
  /** The whole plan. Without one (≤ 0) only the value is shown. */
  total: number;
  /** Where it should be by now: the tick on the bar. */
  due: number;
  tone: Tone | undefined;
  format: (n: number) => string;
  /** Verb for the tooltip: "Gastado", "Obtenido"… */
  doneLabel: string;
  /** What is being counted ("clics", "impresiones"…), small after the figures. */
  unit?: string;
}

/** "value / total" over a progress bar: the fill is value over total, the
 *  tick marks where it should be by now. Coloured by the row's tone. */
const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  total,
  due: dueValue,
  tone,
  format,
  doneLabel,
  unit,
}) => {
  const theme = useTheme();
  const unitTag = unit ? (
    <Box
      component="span"
      sx={{ color: "text.secondary", fontSize: "0.75em", ml: 0.5 }}
    >
      {unit}
    </Box>
  ) : null;
  if (!(total > 0))
    return (
      <>
        {format(value)}
        {unitTag}
      </>
    );
  const spent = Math.min(1, Math.max(0, value / total));
  const due = Math.min(1, Math.max(0, dueValue / total));
  return (
    <Tooltip
      title={`${doneLabel} ${fmtPct((value / total) * 100)} · previsto hasta ayer ${format(dueValue)} (${fmtPct(due * 100)})`}
    >
      <Box component="span" sx={{ display: "block" }}>
        <Box component="span">
          <Box component="span" sx={{ fontWeight: 700 }}>
            {format(value)}
          </Box>
          <Box component="span" sx={{ color: "text.secondary" }}>
            {" / "}
            {format(total)}
          </Box>
          {unitTag}
        </Box>
        <Box
          component="span"
          sx={{
            display: "block",
            position: "relative",
            height: 4,
            mt: 0.5,
            borderRadius: 2,
            backgroundColor: "action.selected",
          }}
        >
          <Box
            component="span"
            sx={{
              position: "absolute",
              inset: 0,
              width: `${spent * 100}%`,
              borderRadius: 2,
              backgroundColor: tone
                ? TONE_STYLE[theme.palette.mode][tone].color
                : "primary.main",
            }}
          />
          {due > 0 && (
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: -2,
                bottom: -2,
                left: `calc(${due * 100}% - 1px)`,
                width: 2,
                backgroundColor: "text.primary",
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Column registry
// ──────────────────────────────────────────────────────────────────────────────

export const ALL_COLUMNS: ColumnDef[] = [
  // ── Identification ──────────────────────────────────────────────────────────
  {
    id: "companyName",
    sector: "identification",
    label: "Cliente",
    defaultVisible: true,
    pinned: true,
    render: (r) => r.companyName || "—",
    csvValue: (r) => r.companyName || "",
  },
  {
    id: "associatedCampaignName",
    sector: "identification",
    label: "Subcampaña",
    defaultVisible: true,
    pinned: true,
    render: (r) => (
      <Link
        href={`/briefings/${r.briefingId}/associated-campaigns/${r.associatedCampaignId}#margin`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "inherit",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {r.associatedCampaignName}
      </Link>
    ),
    csvValue: (r) => r.associatedCampaignName,
  },
  {
    id: "platform",
    sector: "identification",
    label: "Plataforma",
    defaultVisible: true,
    pinned: true,
    render: (r) => r.platform,
    csvValue: (r) => r.platform,
  },
  {
    id: "briefingCampaignType",
    sector: "identification",
    label: "Tipo Plataforma",
    defaultVisible: true,
    pinned: true,
    render: (r) => r.briefingCampaignType ?? "—",
    csvValue: (r) => r.briefingCampaignType ?? "",
  },
  {
    id: "realPlatform",
    sector: "identification",
    label: "Proveedor real",
    defaultVisible: true,
    adminOnly: true,
    defaultWidth: 130,
    render: (r) => realPlatformLabel(r) || "—",
    csvValue: (r) => realPlatformLabel(r),
  },
  {
    id: "briefingName",
    sector: "identification",
    label: "Briefing",
    defaultVisible: true,
    render: (r) => r.briefingName,
    csvValue: (r) => r.briefingName,
  },
  {
    id: "sourceId",
    sector: "identification",
    label: "Source ID",
    defaultVisible: true,
    render: (r) => <MonoSpan>{r.sourceId || "—"}</MonoSpan>,
    csvValue: (r) => r.sourceId ?? "",
  },
  {
    id: "investmentPayment",
    sector: "identification",
    label: "Quién paga",
    defaultVisible: true,
    render: (r) =>
      r.investmentPayment
        ? (INVESTMENT_PAYMENT_LABEL[r.investmentPayment] ?? r.investmentPayment)
        : "—",
    csvValue: (r) =>
      r.investmentPayment
        ? (INVESTMENT_PAYMENT_LABEL[r.investmentPayment] ?? r.investmentPayment)
        : "",
  },

  // ── Brief ───────────────────────────────────────────────────────────────────
  {
    id: "startDate",
    sector: "brief",
    label: "Inicio",
    defaultVisible: true,
    render: (r) => fmtDate(r.startDate),
    csvValue: (r) => fmtDate(r.startDate),
  },
  {
    id: "endDate",
    sector: "brief",
    label: "Fin",
    defaultVisible: true,
    render: (r) => fmtDate(r.endDate),
    csvValue: (r) => fmtDate(r.endDate),
  },
  {
    id: "objective",
    sector: "brief",
    label: "Objetivo",
    defaultVisible: true,
    render: (r) => r.objective,
    csvValue: (r) => r.objective,
  },
  {
    id: "grossUnitCost",
    sector: "brief",
    label: "Coste Unit Cliente",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoneyDetailed(r.grossUnitCost),
    csvValue: (r) => csvNum(r.grossUnitCost, 4),
  },
  {
    id: "netUnitCost",
    sector: "brief",
    label: "Coste Unit Plataforma",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoneyDetailed(r.netUnitCost),
    csvValue: (r) => csvNum(r.netUnitCost, 4),
  },
  {
    id: "realUnitCostPlatform",
    sector: "brief",
    label: "Coste Unit Plataforma (real)",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoneyDetailed(r.realUnitCostPlatform),
    csvValue: (r) => csvNum(r.realUnitCostPlatform, 4),
  },
  {
    id: "totalInversion",
    sector: "brief",
    label: "Inversión Cliente",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoney(r.totalInversion, 2),
    csvValue: (r) => r.totalInversion,
  },
  {
    id: "margin",
    sector: "brief",
    label: "Margen %",
    align: "right",
    defaultVisible: true,
    // Paid media que paga el cliente: sin margen, se cobra fee aparte.
    render: (r) => (r.marginApplies === false ? "Fee" : fmtPct(r.margin)),
    csvValue: (r) => (r.marginApplies === false ? "Fee" : csvNum(r.margin)),
  },
  {
    id: "marginIdeal",
    sector: "brief",
    label: "Margen % (ideal)",
    align: "right",
    defaultVisible: true,
    ideal: true,
    editable: "margin",
    defaultWidth: 130,
    tone: (r) =>
      r.marginIdeal != null && r.marginIdeal < 0 ? "bad" : undefined,
    render: (r) => (r.marginIdeal == null ? "—" : fmtPct(r.marginIdeal)),
    csvValue: (r) => (r.marginIdeal == null ? "" : csvNum(r.marginIdeal)),
  },
  {
    id: "platformInversion",
    sector: "brief",
    label: "Inversión Plataforma Inicial",
    align: "right",
    defaultVisible: true,
    defaultWidth: 130,
    render: (r) => fmtMoneyDetailed(r.platformInversion, 2),
    csvValue: (r) => csvNum(r.platformInversion),
  },
  {
    // Sigue al margen guardado: cambia cada vez que se acepta uno nuevo.
    id: "currentPlatformInversion",
    sector: "brief",
    label: "Inversión Plataforma Actual",
    align: "right",
    defaultVisible: true,
    defaultWidth: 130,
    render: (r) => fmtMoneyDetailed(r.currentPlatformInversion, 2),
    csvValue: (r) => csvNum(r.currentPlatformInversion),
  },
  {
    id: "targetVolume",
    sector: "brief",
    label: "Volumen Previsto",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.targetVolume),
    csvValue: (r) => Math.round(r.targetVolume),
  },

  // ── KPIs Generales ──────────────────────────────────────────────────────────
  {
    id: "impressions",
    sector: "kpis",
    editable: "manual",
    manualField: "impressions",
    label: "Impresiones",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.impressions),
    csvValue: (r) => r.impressions,
  },
  {
    id: "clicks",
    sector: "kpis",
    editable: "manual",
    manualField: "clicks",
    label: "Clicks reales (hasta ayer)",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.clicks),
    csvValue: (r) => r.clicks,
  },
  {
    id: "completeViews",
    sector: "kpis",
    editable: "manual",
    manualField: "completeViews",
    label: "Visualizaciones",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.completeViews),
    csvValue: (r) => r.completeViews,
  },
  {
    id: "conversions",
    sector: "kpis",
    editable: "manual",
    manualField: "conversions",
    label: "Conversiones",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.conversions),
    csvValue: (r) => r.conversions,
  },
  {
    id: "engagements",
    sector: "kpis",
    label: "Interacciones",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.engagements),
    csvValue: (r) => r.engagements,
  },
  {
    id: "engagementRate",
    sector: "kpis",
    label: "% Interacción",
    align: "right",
    defaultVisible: false,
    render: (r) =>
      r.engagementRate == null ? "—" : fmtPct(r.engagementRate * 100, 2),
    csvValue: (r) =>
      r.engagementRate == null ? "" : csvNum(r.engagementRate * 100, 2),
  },
  {
    id: "uniqueUsers",
    sector: "kpis",
    editable: "manual",
    manualField: "uniqueUsers",
    label: "Alcance (usuarios únicos)",
    align: "right",
    defaultVisible: false,
    defaultWidth: 140,
    render: (r) => (r.uniqueUsers == null ? "—" : fmtInt(r.uniqueUsers)),
    csvValue: (r) => (r.uniqueUsers == null ? "" : Math.round(r.uniqueUsers)),
  },
  {
    id: "frequency",
    sector: "kpis",
    label: "Frecuencia",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.frequency == null ? "—" : fmtNum(r.frequency, 2)),
    csvValue: (r) => (r.frequency == null ? "" : csvNum(r.frequency, 2)),
  },
  {
    id: "measurableImpressions",
    sector: "kpis",
    label: "Impr. Medibles",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.measurableImpressions),
    csvValue: (r) => r.measurableImpressions,
  },
  {
    id: "viewableImpressions",
    sector: "kpis",
    label: "Impr. Visibles",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.viewableImpressions),
    csvValue: (r) => r.viewableImpressions,
  },
  {
    id: "viewabilityRate",
    sector: "kpis",
    label: "% Visibilidad",
    align: "right",
    defaultVisible: false,
    render: (r) =>
      r.viewabilityRate == null ? "—" : fmtPct(r.viewabilityRate * 100),
    csvValue: (r) =>
      r.viewabilityRate == null ? "" : csvNum(r.viewabilityRate * 100),
  },
  {
    id: "firstQuartileViews",
    sector: "kpis",
    label: "Vistas 25%",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.firstQuartileViews),
    csvValue: (r) => r.firstQuartileViews,
  },
  {
    id: "midpointViews",
    sector: "kpis",
    label: "Vistas 50%",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.midpointViews),
    csvValue: (r) => r.midpointViews,
  },
  {
    id: "thirdQuartileViews",
    sector: "kpis",
    label: "Vistas 75%",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.thirdQuartileViews),
    csvValue: (r) => r.thirdQuartileViews,
  },

  // ── Alerta ──────────────────────────────────────────────────────────────────
  {
    id: "status",
    sector: "alert",
    label: "Estado",
    defaultVisible: true,
    emphasis: true,
    render: (r) => <StatusChip status={r.status} />,
    csvValue: (r) => r.status,
  },
  {
    id: "projection",
    sector: "alert",
    label: "Proyección",
    defaultVisible: true,
    emphasis: true,
    defaultWidth: 150,
    render: (r) => <ProjectionChip row={r} />,
    csvValue: (r) =>
      r.projectionZeroData
        ? PROJECTION_ZERO_LABEL
        : r.projectionVariation === null
          ? ""
          : csvNum(r.projectionVariation * 100),
  },
  {
    id: "expectedVolumePercent",
    sector: "alert",
    label: "% Vol. Esperado Hasta Ayer",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtPct(r.expectedVolumePercent * 100),
    csvValue: (r) => csvNum(r.expectedVolumePercent * 100),
  },
  {
    id: "expectedVolumeUntilYesterday",
    sector: "alert",
    label: "Vol. Esperado Hasta Ayer",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.expectedVolumeUntilYesterday),
    csvValue: (r) => Math.round(r.expectedVolumeUntilYesterday),
  },
  {
    id: "obtainedVolumePercent",
    sector: "alert",
    label: "% Vol. Obtenido Hasta Ayer",
    align: "right",
    defaultVisible: true,
    tone: (r) => byVolumeBand(r.obtainedVolumePercent),
    render: (r) => fmtPct(r.obtainedVolumePercent * 100),
    csvValue: (r) => csvNum(r.obtainedVolumePercent * 100),
  },
  {
    id: "accumulatedVolumeUntilYesterday",
    sector: "alert",
    label: "Vol. Obtenido Hasta Ayer",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtInt(r.accumulatedVolumeUntilYesterday),
    csvValue: (r) => Math.round(r.accumulatedVolumeUntilYesterday),
  },
  {
    // Impresiones reales a cierre de ayer sea cual sea el objetivo: en una
    // línea CPV/CPC el volumen obtenido no son impresiones y esta sí.
    id: "impressionsUntilYesterday",
    sector: "alert",
    label: "Impr. Obtenidas Hasta Ayer",
    align: "right",
    defaultVisible: true,
    defaultWidth: 130,
    render: (r) => fmtInt(r.impressions),
    csvValue: (r) => r.impressions,
  },
  {
    // Sin tone: el color va en la barra, no en el fondo de la celda.
    id: "volumeProgress",
    sector: "alert",
    label: "Vol. Obtenido / Previsto",
    align: "right",
    defaultVisible: true,
    defaultWidth: 200,
    render: (r) => (
      <ProgressBar
        value={r.accumulatedVolumeUntilYesterday}
        total={r.targetVolume}
        due={r.expectedVolumeUntilYesterday}
        tone={volumeTone(r)}
        format={fmtInt}
        doneLabel="Obtenido"
        unit={VOLUME_LABEL[r.objective]}
      />
    ),
    csvValue: (r) =>
      r.targetVolume > 0
        ? csvNum((r.accumulatedVolumeUntilYesterday / r.targetVolume) * 100)
        : "",
  },
  {
    id: "volumeVariation",
    sector: "alert",
    label: "Variación Volumen",
    align: "right",
    defaultVisible: true,
    emphasis: true,
    tone: (r) => bySign(r.volumeVariation),
    render: (r) => fmtInt(r.volumeVariation),
    csvValue: (r) => Math.round(r.volumeVariation),
  },

  // ── Pacing Inversión ────────────────────────────────────────────────────────
  {
    id: "expectedPlatformInvestmentUntilYesterday",
    sector: "pacing",
    label: "Inv. Prevista Hasta Ayer",
    align: "right",
    defaultVisible: true,
    defaultWidth: 130,
    render: (r) => fmtMoney(r.expectedPlatformInvestmentUntilYesterday, 2),
    csvValue: (r) => csvNum(r.expectedPlatformInvestmentUntilYesterday),
  },
  {
    // Mismo gasto que "Coste Total Plataforma" (las métricas ya llegan cerradas
    // a ayer), puesto al lado de la prevista para compararlas de un vistazo.
    id: "realPlatformInvestmentUntilYesterday",
    sector: "pacing",
    label: "Inv. Real Hasta Ayer",
    align: "right",
    defaultVisible: true,
    defaultWidth: 130,
    tone: spendTone,
    render: (r) => fmtMoney(r.totalPlatformCost, 2),
    csvValue: (r) => csvNum(r.totalPlatformCost),
  },
  {
    // Sin tone: el color va en la barra, no en el fondo de la celda.
    id: "platformBudgetProgress",
    sector: "pacing",
    label: "Gasto / Inversión Plataforma Actual",
    align: "right",
    defaultVisible: true,
    defaultWidth: 220,
    render: (r) => (
      <ProgressBar
        value={r.totalPlatformCost}
        total={r.currentPlatformInversion}
        due={r.expectedPlatformInvestmentUntilYesterday}
        tone={spendTone(r)}
        format={(n) => fmtMoney(n, 2)}
        doneLabel="Gastado"
      />
    ),
    csvValue: (r) =>
      r.currentPlatformInversion > 0
        ? csvNum((r.totalPlatformCost / r.currentPlatformInversion) * 100)
        : "",
  },
  {
    id: "totalPlatformCost",
    sector: "pacing",
    editable: "manual",
    manualField: "platformInversion",
    label: "Coste Total Plataforma",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoney(r.totalPlatformCost, 2),
    csvValue: (r) => r.totalPlatformCost,
  },
  {
    id: "pacingInvestmentPercent",
    sector: "pacing",
    label: "Pacing Inv. %",
    align: "right",
    defaultVisible: true,
    emphasis: true,
    tone: (r) => byPacingTarget(r.currentPacingInvestmentPercent),
    render: (r) => fmtPct(r.currentPacingInvestmentPercent * 100),
    csvValue: (r) => csvNum(r.currentPacingInvestmentPercent * 100),
  },
  {
    id: "yesterdayPacingInvestmentVar",
    sector: "pacing",
    label: "Variación vs Ayer",
    align: "right",
    defaultVisible: true,
    tone: (r) => bySign(r.yesterdayPacingInvestmentVar),
    render: (r) => fmtPct(r.yesterdayPacingInvestmentVar * 100),
    csvValue: (r) => csvNum(r.yesterdayPacingInvestmentVar * 100),
  },
  {
    // Coste unitario REAL a ayer (columna AX del Excel WIP). null mientras no
    // haya coste real acumulado → fmtMoney pinta "—".
    id: "investmentNeededTotalReal",
    sector: "pacing",
    label: "Inv. Plat. Nec. (Total)",
    align: "right",
    defaultVisible: true,
    action: "acceptIdeal",
    defaultWidth: 150,
    // Con un margen ideal editado muestra la inversión que dejaría ese margen.
    render: (r) =>
      fmtMoney(r.idealInvestment ?? r.investmentNeededTotalReal, 2),
    // CSV: celda vacía (no "—") para null, así Excel la trata como numérica/blanca.
    csvValue: (r) =>
      r.investmentNeededTotalReal == null ? "" : r.investmentNeededTotalReal,
  },
  {
    // Coste unitario REAL a ayer (columna AW del Excel WIP). null si no hay
    // coste real todavía → "—" en pantalla, celda vacía en CSV.
    id: "investmentNeededRemainingReal",
    sector: "pacing",
    label: "Inv. Plat. Nec. (Restante)",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoney(r.investmentNeededRemainingReal, 2),
    csvValue: (r) =>
      r.investmentNeededRemainingReal == null
        ? ""
        : r.investmentNeededRemainingReal,
  },
  {
    id: "realUnitCostClient",
    sector: "pacing",
    label: "CU Cliente (real)",
    align: "right",
    defaultVisible: true,
    render: (r) => fmtMoneyDetailed(r.realUnitCostClient),
    csvValue: (r) => csvNum(r.realUnitCostClient, 4),
  },
  {
    id: "accumulatedClientCost",
    sector: "pacing",
    label: "Coste Cliente Acumulado",
    align: "right",
    defaultVisible: false,
    defaultWidth: 140,
    render: (r) => fmtMoney(r.accumulatedClientCost, 2),
    csvValue: (r) => r.accumulatedClientCost,
  },
  {
    id: "cpmReal",
    sector: "pacing",
    label: "CPM real",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.cpmReal == null ? "—" : fmtMoneyDetailed(r.cpmReal)),
    csvValue: (r) => (r.cpmReal == null ? "" : csvNum(r.cpmReal, 4)),
  },
  {
    id: "cpcReal",
    sector: "pacing",
    label: "CPC real",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.cpcReal == null ? "—" : fmtMoneyDetailed(r.cpcReal)),
    csvValue: (r) => (r.cpcReal == null ? "" : csvNum(r.cpcReal, 4)),
  },
  {
    id: "cpvReal",
    sector: "pacing",
    label: "CPV real",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.cpvReal == null ? "—" : fmtMoneyDetailed(r.cpvReal)),
    csvValue: (r) => (r.cpvReal == null ? "" : csvNum(r.cpvReal, 4)),
  },
  {
    id: "cpaReal",
    sector: "pacing",
    label: "CPA real",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.cpaReal == null ? "—" : fmtMoneyDetailed(r.cpaReal)),
    csvValue: (r) => (r.cpaReal == null ? "" : csvNum(r.cpaReal, 4)),
  },
  {
    id: "cpeReal",
    sector: "pacing",
    label: "CPE real",
    align: "right",
    defaultVisible: false,
    render: (r) => (r.cpeReal == null ? "—" : fmtMoneyDetailed(r.cpeReal)),
    csvValue: (r) => (r.cpeReal == null ? "" : csvNum(r.cpeReal, 4)),
  },

  // ── Others (hidden by default) ───────────────────────────────────────────────
  {
    id: "briefingId",
    sector: "others",
    label: "ID Briefing",
    defaultVisible: false,
    render: (r) => <MonoSpan size={11}>{r.briefingId}</MonoSpan>,
    csvValue: (r) => r.briefingId,
  },
  {
    id: "concatenado",
    sector: "others",
    label: "Concatenado",
    defaultVisible: false,
    render: (r) => <MonoSpan size={11}>{r.concatenado}</MonoSpan>,
    csvValue: (r) => r.concatenado,
  },
  {
    id: "dataSource",
    sector: "others",
    label: "Origen datos",
    defaultVisible: false,
    render: (r) => (r.isDirectBuy ? "Manual (compra directa)" : "API"),
    csvValue: (r) => (r.isDirectBuy ? "Manual (compra directa)" : "API"),
  },
  {
    id: "briefingType",
    sector: "others",
    label: "Tipo Briefing",
    defaultVisible: false,
    render: (r) => r.briefingType,
    csvValue: (r) => r.briefingType,
  },
  {
    id: "campaignFormat",
    sector: "others",
    label: "Formato",
    defaultVisible: false,
    render: (r) => r.campaignFormat ?? "—",
    csvValue: (r) => r.campaignFormat ?? "",
  },
  {
    id: "currency",
    sector: "others",
    label: "Moneda",
    defaultVisible: false,
    render: (r) => r.currency ?? "—",
    csvValue: (r) => r.currency ?? "",
  },
  {
    id: "adserverCost",
    sector: "others",
    label: "Ad Serving",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.adserverCost),
    csvValue: (r) => csvNum(r.adserverCost, 4),
  },
  {
    id: "zeotapDataCost",
    sector: "others",
    label: "Data Proveedor",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.zeotapDataCost),
    csvValue: (r) => csvNum(r.zeotapDataCost, 4),
  },
  {
    id: "impressionTrackerCost",
    sector: "others",
    label: "Impr Tracker",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.impressionTrackerCost),
    csvValue: (r) => csvNum(r.impressionTrackerCost, 4),
  },
  {
    id: "clickTrackerCost",
    sector: "others",
    label: "Click Tracker",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.clickTrackerCost),
    csvValue: (r) => csvNum(r.clickTrackerCost, 4),
  },
  {
    id: "adtechCost",
    sector: "others",
    label: "Ad Tech",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.adtechCost),
    csvValue: (r) => csvNum(r.adtechCost, 4),
  },
  {
    id: "addedCostsCpm",
    sector: "others",
    label: "Costes Añadidos (CPM)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoneyDetailed(r.addedCostsCpm),
    csvValue: (r) => csvNum(r.addedCostsCpm, 4),
  },
  {
    id: "addedCostsTotal",
    sector: "others",
    label: "Costes Añadidos Totales",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.addedCostsTotal, 2),
    csvValue: (r) => r.addedCostsTotal,
  },
  {
    id: "totalDays",
    sector: "others",
    label: "Días Totales",
    align: "right",
    defaultVisible: false,
    render: (r) => r.totalDays,
    csvValue: (r) => r.totalDays,
  },
  {
    id: "elapsedDays",
    sector: "others",
    label: "Días Transcurridos",
    align: "right",
    defaultVisible: false,
    render: (r) => r.elapsedDays,
    csvValue: (r) => r.elapsedDays,
  },
  {
    id: "remainingDays",
    sector: "others",
    label: "Días Restantes",
    align: "right",
    defaultVisible: false,
    render: (r) => r.remainingDays,
    csvValue: (r) => r.remainingDays,
  },
  {
    id: "timePercent",
    sector: "others",
    label: "% Tiempo",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.timePercent * 100),
    csvValue: (r) => csvNum(r.timePercent * 100),
  },
  {
    id: "accumulatedVolume",
    sector: "others",
    label: "Volumen Acumulado",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.accumulatedVolume),
    csvValue: (r) => Math.round(r.accumulatedVolume),
  },
  {
    id: "remainingVolume",
    sector: "others",
    label: "Volumen Restante",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.remainingVolume),
    csvValue: (r) => Math.round(r.remainingVolume),
  },
  {
    id: "dailyVolumeNeeded",
    sector: "others",
    label: "Diario Necesario",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.dailyVolumeNeeded),
    csvValue: (r) => Math.round(r.dailyVolumeNeeded),
  },
  {
    id: "volumePercent",
    sector: "others",
    label: "% Volumen",
    align: "right",
    defaultVisible: false,
    tone: (r) => byVolumeBand(r.volumePercent),
    render: (r) => fmtPct(r.volumePercent * 100),
    csvValue: (r) => csvNum(r.volumePercent * 100),
  },
  {
    id: "volumeVariationPercent",
    sector: "others",
    label: "Variación Volumen %",
    align: "right",
    defaultVisible: false,
    tone: (r) => bySign(r.volumeVariationPercent),
    render: (r) => fmtPct(r.volumeVariationPercent * 100),
    csvValue: (r) => csvNum(r.volumeVariationPercent * 100),
  },
  {
    id: "accumulatedInvestment",
    sector: "others",
    label: "Inv. Acumulada",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.accumulatedInvestment, 2),
    csvValue: (r) => r.accumulatedInvestment,
  },
  {
    id: "remainingInvestment",
    sector: "others",
    label: "Inv. Restante",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.remainingInvestment, 2),
    csvValue: (r) => r.remainingInvestment,
  },
  {
    id: "remainingPlatformInvestment",
    sector: "others",
    label: "Inv. Plat. Restante",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.remainingPlatformInvestment, 2),
    csvValue: (r) => r.remainingPlatformInvestment,
  },
  {
    // Coste unitario PLANIFICADO (netUnitCost). Se conserva, pero la cabecera
    // deja claro que no es "al real".
    id: "investmentNeededTotal",
    sector: "others",
    label: "Inv. Plat. Nec. (Total plan.)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.investmentNeededTotal, 2),
    csvValue: (r) => r.investmentNeededTotal,
  },
  {
    // Coste unitario PLANIFICADO (netUnitCost), versión restante.
    id: "investmentNeededRemaining",
    sector: "others",
    label: "Inv. Plat. Nec. (Restante plan.)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.investmentNeededRemaining, 2),
    csvValue: (r) => r.investmentNeededRemaining,
  },
  {
    id: "pacingPercent",
    sector: "others",
    label: "Pacing %",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.pacingPercent),
    csvValue: (r) => csvNum(r.pacingPercent),
  },
  {
    id: "yesterdayImpressions",
    sector: "others",
    label: "Impresiones Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayImpressions),
    csvValue: (r) => r.yesterdayImpressions,
  },
  {
    id: "yesterdayClicks",
    sector: "others",
    label: "Clicks Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayClicks),
    csvValue: (r) => r.yesterdayClicks,
  },
  {
    id: "yesterdayCompleteViews",
    sector: "others",
    label: "CV Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayCompleteViews),
    csvValue: (r) => r.yesterdayCompleteViews,
  },
  {
    id: "yesterdayConversions",
    sector: "others",
    label: "Conversiones Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayConversions),
    csvValue: (r) => r.yesterdayConversions,
  },
  {
    id: "yesterdayEngagements",
    sector: "others",
    label: "Interacciones Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayEngagements),
    csvValue: (r) => r.yesterdayEngagements,
  },
  {
    id: "yesterdayRealCost",
    sector: "others",
    label: "Coste Real Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.yesterdayRealCost, 2),
    csvValue: (r) => r.yesterdayRealCost,
  },
  {
    id: "dailyTargetVolume",
    sector: "others",
    label: "Objetivo Diario",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.dailyTargetVolume),
    csvValue: (r) => Math.round(r.dailyTargetVolume),
  },
  {
    id: "yesterdayActualVolume",
    sector: "others",
    label: "Vol. Real Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayActualVolume),
    csvValue: (r) => Math.round(r.yesterdayActualVolume),
  },
  {
    id: "yesterdayVolumeDiff",
    sector: "others",
    label: "Vol. Ayer (Diff)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtInt(r.yesterdayVolumeDiff),
    csvValue: (r) => Math.round(r.yesterdayVolumeDiff),
  },
  {
    id: "yesterdayVolumeVar",
    sector: "others",
    label: "Vol. Ayer (Var%)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.yesterdayVolumeVar * 100),
    csvValue: (r) => csvNum(r.yesterdayVolumeVar * 100),
  },
  {
    id: "yesterdayClientCost",
    sector: "others",
    label: "CC Ayer (margen)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.yesterdayClientCost, 2),
    csvValue: (r) => r.yesterdayClientCost,
  },
  {
    id: "yesterdayEstimatedClientCost",
    sector: "others",
    label: "CC Estim. Ayer",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtMoney(r.yesterdayEstimatedClientCost, 2),
    csvValue: (r) => r.yesterdayEstimatedClientCost,
  },
  {
    id: "viewThroughRate",
    sector: "others",
    label: "% VIS (VTR)",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.viewThroughRate * 100, 2),
    csvValue: (r) => csvNum(r.viewThroughRate * 100, 2),
  },
  {
    id: "clickThroughRate",
    sector: "others",
    label: "% CTR",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.clickThroughRate * 100, 2),
    csvValue: (r) => csvNum(r.clickThroughRate * 100, 2),
  },
  {
    id: "conversionRate",
    sector: "others",
    label: "Conv. Rate",
    align: "right",
    defaultVisible: false,
    render: (r) => fmtPct(r.conversionRate * 100, 2),
    csvValue: (r) => csvNum(r.conversionRate * 100, 2),
  },
];

/** IDs of all columns that are visible by default */
export const DEFAULT_VISIBLE_IDS: string[] = ALL_COLUMNS.filter(
  (c) => c.defaultVisible
).map((c) => c.id);

/** The registry minus the admin-only columns for everybody else. */
export const columnsFor = (isAdmin: boolean): ColumnDef[] =>
  isAdmin ? ALL_COLUMNS : ALL_COLUMNS.filter((c) => !c.adminOnly);
